"use client";

/**
 * 리스트 신청의 실제 유입 출처(first-touch)를 캡처한다.
 *
 * - 최초 방문 시 referrer/UTM/landing 을 localStorage 에 1회 저장하고, 이후에는
 *   덮어쓰지 않는다(= first-touch). 며칠 뒤 사이트 내부에서 재방문해 신청해도
 *   최초 유입 출처가 유지된다.
 * - 키는 snake_case 로 둔다. 백엔드가 djangorestframework-camel-case 파서를 쓰는데,
 *   snake_case 키는 파서를 그대로 통과하므로 중첩 변환 동작과 무관하게 안전하다.
 */

const STORAGE_KEY = "sr_first_touch_traffic_source_v1";

export interface FirstTouchTrafficSource {
  referrer_host: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  landing_path: string;
  first_seen_at: string;
}

function getReferrerHost(): string {
  if (typeof document === "undefined" || !document.referrer) return "";
  try {
    return new URL(document.referrer).host;
  } catch {
    return "";
  }
}

function buildFirstTouch(): FirstTouchTrafficSource {
  const params = new URLSearchParams(window.location.search);
  return {
    referrer_host: getReferrerHost(),
    utm_source: params.get("utm_source"),
    utm_medium: params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    landing_path: window.location.pathname,
    first_seen_at: new Date().toISOString(),
  };
}

/**
 * 저장된 first-touch 유입 출처를 반환한다. 없으면 현재 방문 기준으로 1회 생성·저장한다.
 * 서버 사이드이거나 localStorage 접근이 막힌 환경에서는 null 을 반환한다(백엔드는
 * null 을 "미수집"으로 안전하게 처리).
 */
export function getOrCreateFirstTouchTrafficSource(): FirstTouchTrafficSource | null {
  if (typeof window === "undefined") return null;

  try {
    const cached = window.localStorage.getItem(STORAGE_KEY);
    if (cached) {
      return JSON.parse(cached) as FirstTouchTrafficSource;
    }
    const next = buildFirstTouch();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
  } catch {
    return null;
  }
}
